import { useEffect } from 'react';

import {
  MarketplaceSubEvent,
  OfferDashboardSubEvent,
} from '../../../../../../../services/analytics/marketplaceEvents';
import { useOfferAnalytics } from '../OfferAnalyticsContext';
import { MarketplaceOffer, OfferAction, OfferActionKind } from '../utils';
import AcceptOfferModal from './AcceptOfferModal';
import AdjustCounterModal from './AdjustCounterModal';
import CounterModal from './CounterModal';
import WithdrawCounterModal from './WithdrawCounterModal';

type OfferActionModalsProps = {
  action: OfferAction | null;
  liveOffers: MarketplaceOffer[];
  externalId: string;
  dealId: string;
  payoffAmount: number | null;
  payoffIsTrusted: boolean;
  onClose: () => void;
  /** Fires after a driver action lands (accept/counter/adjust/withdraw succeeded), before close. */
  onSuccess: () => void;
};

// Each driver-action modal fires its own "modal opened" event to measure open-to-action drop-off.
const MODAL_OPENED_EVENTS: Record<OfferActionKind, OfferDashboardSubEvent> = {
  accept: MarketplaceSubEvent.OfferAcceptModalOpened,
  counter: MarketplaceSubEvent.OfferCounterModalOpened,
  adjust: MarketplaceSubEvent.OfferAdjustModalOpened,
  withdraw: MarketplaceSubEvent.OfferWithdrawModalOpened,
};

// Single host for the driver-action modals, lifted out of OfferCard so an open modal survives a
// background poll dropping its card. Only the active modal is mounted; closing unmounts it.
const OfferActionModals = ({
  action,
  liveOffers,
  externalId,
  dealId,
  payoffAmount,
  payoffIsTrusted,
  onClose,
  onSuccess,
}: OfferActionModalsProps) => {
  const { trackOfferEvent } = useOfferAnalytics();

  const kind = action?.kind ?? null;
  const openedOfferId = action?.card.offer.id;
  const openedSurface = action?.surface;

  useEffect(() => {
    if (action == null) return;
    trackOfferEvent(MODAL_OPENED_EVENTS[action.kind], {
      offerId: action.card.offer.id,
      dealerLabel: action.card.dealerLabel,
      dealerAmount: action.card.amount,
      surface: action.surface,
    });
    // Re-fires only when a different modal/offer/surface opens, not on poll re-renders.
  }, [kind, openedOfferId, openedSurface, trackOfferEvent]);

  if (action == null) return null;

  const { card, surface } = action;
  const shared = { card, surface, liveOffers, externalId, onClose, onSuccess };

  if (kind === 'accept')
    return (
      <AcceptOfferModal
        {...shared}
        dealId={dealId}
        payoffAmount={payoffAmount}
        payoffIsTrusted={payoffIsTrusted}
      />
    );
  if (kind === 'counter') return <CounterModal {...shared} />;
  if (kind === 'adjust') return <AdjustCounterModal {...shared} />;
  return <WithdrawCounterModal {...shared} />;
};

export default OfferActionModals;
